<template>
  <BottomSheet
    :model-value="modelValue"
    :title="title"
    :test-id="testId"
    @update:model-value="emit('update:modelValue', $event)"
  >
    <div class="pb-2">
      <template v-for="group in groups" :key="group.name || '_'">
        <p
          v-if="group.name"
          class="px-1 pb-1 pt-3 text-[11px] font-bold uppercase tracking-wide text-gray-500"
          :data-testid="`${testId}-section-${group.name}`"
        >
          {{ group.name }}
        </p>
        <button
          v-for="item in group.items"
          :key="item.id"
          type="button"
          class="flex min-h-[52px] w-full items-center gap-3 rounded-lg px-1 py-2 text-left disabled:cursor-not-allowed disabled:opacity-50"
          :class="item.danger ? 'text-negative' : 'text-gray-800'"
          :disabled="item.disabled"
          :data-testid="`${testId}-item-${item.id}`"
          @click="choose(item)"
        >
          <svg v-if="item.icon" viewBox="0 0 24 24" class="h-5 w-5 shrink-0" aria-hidden="true">
            <path :d="item.icon" fill="currentColor" />
          </svg>
          <span class="min-w-0 flex-1">
            <span class="block text-sm font-semibold">{{ item.label }}</span>
            <span v-if="helpOf(item)" class="block text-xs font-normal text-gray-500">{{
              helpOf(item)
            }}</span>
          </span>
        </button>
      </template>
    </div>
    <template #footer>
      <button
        type="button"
        class="h-11 w-full rounded-lg border border-solid border-gray-300 text-sm font-semibold text-gray-700"
        :data-testid="`${testId}-dismiss`"
        @click="emit('update:modelValue', false)"
      >
        Fechar
      </button>
    </template>
  </BottomSheet>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import BottomSheet from '@/components/designSystem/BottomSheet/BottomSheet.vue'
import type { ActionSheetItem } from './types'

const props = withDefaults(
  defineProps<{ modelValue: boolean; title?: string; items: ActionSheetItem[]; testId?: string }>(),
  { title: undefined, testId: 'action-sheet' }
)
const emit = defineEmits<{ 'update:modelValue': [value: boolean]; select: [id: string] }>()

/** Grupos na ordem dos itens: itens seguidos com a mesma `section` ficam juntos. */
const groups = computed(() =>
  props.items.reduce<{ name: string; items: ActionSheetItem[] }[]>((acc, item) => {
    const name = item.section ?? ''
    const last = acc[acc.length - 1]
    if (last && last.name === name) last.items.push(item)
    else acc.push({ name, items: [item] })
    return acc
  }, [])
)

const helpOf = (item: ActionSheetItem): string | undefined =>
  item.disabled && item.disabledReason ? item.disabledReason : item.description

function choose(item: ActionSheetItem): void {
  if (item.disabled) return
  // Fecha antes de emitir: a ação pode abrir outro overlay (modal de exportação, sheet de busca).
  emit('update:modelValue', false)
  emit('select', item.id)
}
</script>
